import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {OrderAddressModel, OrderModel} from '_types'

// interface CreateAdyenSessionProps {
//     amount: {
//         value?: number
//         currency?: string
//     }
//     reference?: string
//     countryCode?: string
//     returnUrl?: string
// }

interface MakeApplePayPaymentProps {
    order?: OrderModel
    countryCode: string
    paymentData: object
}

/**
 * @name useAdyen
 *
 * @hook
 *
 * @description
 * Custom hook to use adyen.
 *
 * @returns An object containing methods for adyen payments.
 */
const useAdyen = () => {
    const {locale} = useMultiSite()

    const ERROR_RESULT_CODES = ['Cancelled', 'Error', 'Refused']
    const hostname = window?.location?.hostname
    const headers = {
        'Content-Type': 'application/json',
    }

    if (hostname.indexOf('dev.shop') > -1) {
        headers['Authorization'] = 'Basic c3RvcmVmcm9udDpjdWNpbmVsbGlEZXY='
    } else if (hostname.indexOf('stage.shop') > -1) {
        headers['Authorization'] = 'Basic c3RvcmVmcm9udDpjdWNpbmVsbGlRdGE='
    }

    const calculateApplePayToken = (paymentData: object) => {
        return btoa(JSON.stringify(paymentData))
    }

    const formatAddress = (address: OrderAddressModel) => {
        return {
            city: address?.city,
            country: address?.countryCode,
            houseNumberOrName: address?.address2 || '',
            postalCode: address?.postalCode,
            stateOrProvince: address?.stateCode,
            street: address?.address1,
        }
    }

    const splitFullName = (fullName?: string): {firstName: string; lastName: string} => {
        const names = fullName ? fullName.split(' ') : ['']

        return {
            firstName: names.slice(0, -1).join(' '),
            lastName: names.slice(-1).join(' '),
        }
    }

    const makeApplePayPayment = async ({order, countryCode, paymentData}: MakeApplePayPaymentProps) => {
        const host = hostname === 'localhost' ? 'localhost:3000' : hostname

        // customer data
        const billingAddress = order?.billingAddress
        const shippingAddress = order?.shipments?.[0]?.shippingAddress
        const {firstName, lastName} = splitFullName(order?.customerInfo?.customerName)

        // make payment request to Adyen
        const response = await fetch(`${window.location.protocol}//${host}/applePayPayment`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                amount: {
                    currency: order?.currency || '',
                    value: (order?.orderTotal ?? 0) * 100, // price in minor units
                },
                billingAddress: formatAddress(billingAddress),
                countryCode: countryCode,
                deliveryAddress: formatAddress(shippingAddress),
                paymentMethod: {
                    type: 'applepay',
                    applePayToken: calculateApplePayToken(paymentData),
                },
                reference: order?.orderNo,
                returnUrl: order?.c_responseData?.continueUrl,
                shopperEmail: order?.customerInfo?.email,
                shopperLocale: locale?.id?.replace('-', '_'),
                shopperName: {
                    firstName,
                    lastName,
                },
                shopperReference: order?.customerInfo?.customerNo,
            }),
        })

        const responseJson = await response.json()
        const paymentSuccessful = response.ok && !ERROR_RESULT_CODES.includes(responseJson?.resultCode)

        return {
            error: !paymentSuccessful,
            response,
            responseJson,
        }
    }

    // const createAdyenSession = (sessionBody: CreateAdyenSessionProps) => {
    //     const {returnUrl} = sessionBody || ''
    //     // make a session request to Adyen
    //     return fetch(`https://${window?.location?.hostname}/sessions`, {
    //         method: 'POST',
    //         body: {...sessionBody, returnUrl},
    //     })
    // }

    return {
        makeApplePayPayment,
        // createAdyenSession,
    }
}

export {useAdyen}
